// transfer-records.tsx — 页面-转账记录(user.pen frame 08CxQ)
//
// 设计:user.pen frame 08CxQ — 列表(发出 / 收到 双向),最新在上;空态显示"还没有转账记录"。
//
// 数据源:RLS 直读 public.transfer(from_user_id = me OR to_user_id = me;
// 详 database/migrations/20260516001000_rls_and_grants.sql §transfer_select_party)
// + 关联 public.profiles 查对方 username_masked。
//
// 路由:`/wallet/transfer/records`(transfer.tsx 顶部"转账记录"按钮跳过来)。
import { useQuery } from '@tanstack/react-query'
import { getSupabase, useCurrentUser } from '@dogdisk/shared'
import { AppLayout } from '../../components/app-layout'
import { MobileScreen, TopBar } from '../../components/mobile-screen'

type TransferRow = {
  id: string
  from_user_id: string
  to_user_id: string
  amount_coin: number
  note: string | null
  status: 'pending' | 'completed' | 'cancelled'
  created_at: string
  completed_at: string | null
}

type ProfileRow = { user_id: string; username: string }

function useTransferRecords(myUserId: string | undefined) {
  return useQuery({
    queryKey: ['wallet', 'transfer', 'records', myUserId] as const,
    enabled: !!myUserId,
    queryFn: async () => {
      const sb = getSupabase()
      // RLS 限定 from_user_id=me OR to_user_id=me;不需要再加 or filter
      // eslint-disable-next-line no-restricted-syntax -- transfer RLS from/to_user_id=me 收口(只读自己的转账)
      const { data, error } = await sb
        .from('transfer')
        .select('id, from_user_id, to_user_id, amount_coin, note, status, created_at, completed_at')
        .order('created_at', { ascending: false })
        .limit(100)
      if (error) throw error
      const rows = (data ?? []) as TransferRow[]
      // 拉对方 username 做 mask 显示
      const peerIds = Array.from(
        new Set(
          rows.flatMap((r) => [r.from_user_id, r.to_user_id]).filter((id) => id !== myUserId),
        ),
      )
      let peers: Record<string, string> = {}
      if (peerIds.length > 0) {
        // eslint-disable-next-line no-restricted-syntax -- profiles by 对手方 id 读公开档案(展示用户名/头像;public profile RLS)
        const { data: profs } = await sb
          .from('profiles')
          .select('user_id, username')
          .in('user_id', peerIds)
        peers = Object.fromEntries(((profs ?? []) as ProfileRow[]).map((p) => [p.user_id, p.username]))
      }
      return { rows, peers }
    },
    staleTime: 30_000,
  })
}

function maskUsername(name: string | undefined): string {
  if (!name) return '匿名用户'
  if (name.length <= 2) return `${name[0]}***`
  return `${name.slice(0, 2)}***`
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const STATUS_LABEL: Record<TransferRow['status'], string> = {
  pending: '待确认',
  completed: '已完成',
  cancelled: '已取消',
}

const STATUS_TONE: Record<TransferRow['status'], string> = {
  pending: 'bg-amber-50 text-amber-600',
  completed: 'bg-emerald-50 text-emerald-600',
  cancelled: 'bg-gray-100 text-gray-500',
}

export function TransferRecordsPage() {
  const { user } = useCurrentUser()
  const { data, isLoading } = useTransferRecords(user?.user_id)

  const rows = data?.rows ?? []
  const peers = data?.peers ?? {}

  return (
    <AppLayout>
      <MobileScreen bg="surface">
        <TopBar title="转账记录" />
        <main data-testid="08CxQ" className="flex flex-1 flex-col gap-2 px-4 pb-8">
          {isLoading ? (
            <div className="py-12 text-center text-[13px] text-gray-400">加载中…</div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-[13px] text-gray-400">还没有转账记录</div>
          ) : (
            rows.map((r) => {
              const outbound = r.from_user_id === user?.user_id
              const peerId = outbound ? r.to_user_id : r.from_user_id
              return (
                <div key={r.id} className="flex items-center justify-between rounded-xl bg-white px-4 py-3 shadow-sm">
                  <div className="flex flex-col gap-1">
                    <span className="text-[14px] font-medium text-[#1A1A1A]">
                      {outbound ? '→ ' : '← '}
                      {maskUsername(peers[peerId])}
                    </span>
                    <span className="text-[12px] text-gray-500">{formatDate(r.created_at)}</span>
                    {r.note && <span className="text-[12px] text-gray-400">{r.note}</span>}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span
                      className={`text-[15px] font-semibold tabular-nums ${
                        outbound ? 'text-red-500' : 'text-emerald-600'
                      }`}
                    >
                      {outbound ? '-' : '+'}
                      {r.amount_coin.toLocaleString()} COIN
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] ${STATUS_TONE[r.status]}`}>
                      {STATUS_LABEL[r.status]}
                    </span>
                  </div>
                </div>
              )
            })
          )}
        </main>
      </MobileScreen>
    </AppLayout>
  )
}
